import Head from "next/head";
import type { ReactNode } from "react";

export type JsonLd = Record<string, unknown>;

type SeoProps = {
  title: string;
  description?: string;
  keywords?: string;
  children?: ReactNode;
  canonicalUrl?: string;

  /**
   * Defaults to index,follow when omitted.
   * Use for pages that should not be indexed (e.g. newsletter).
   */
  robots?: {
    index?: boolean;
    follow?: boolean;
  };

  openGraph?: {
    title?: string;
    description?: string;
    type?: string;
    image?: string;
    url?: string;
  };

  twitter?: {
    card?: "summary" | "summary_large_image" | "app" | "player";
    title?: string;
    description?: string;
    image?: string;
    url?: string;
  };

  /**
   * Optional JSON-LD blocks; use an array for multiple schemas.
   * Prefer stable @id links and @graph when appropriate.
   */
  jsonLd?: JsonLd[];
};

function toRobotsContent(robots?: SeoProps["robots"]) {
  if (!robots) return undefined;
  const index = robots.index ?? true;
  const follow = robots.follow ?? true;
  return `${index ? "index" : "noindex"}, ${follow ? "follow" : "nofollow"}`;
}

export default function Seo(props: SeoProps) {
  const description = props.description?.trim();

  const canonicalUrl = props.canonicalUrl?.trim();

  const ogTitle = props.openGraph?.title ?? props.title;
  const ogDescription = props.openGraph?.description ?? description;
  const ogType = props.openGraph?.type ?? "website";
  const ogUrl = props.openGraph?.url ?? canonicalUrl;

  const twCard = props.twitter?.card ?? "summary_large_image";
  const twTitle = props.twitter?.title ?? props.title;
  const twDescription = props.twitter?.description ?? description;
  const twUrl = props.twitter?.url ?? canonicalUrl;

  const robotsContent = toRobotsContent(props.robots);

  return (
    <Head>
      <title key="title">{props.title}</title>

      {description ? (
        <meta name="description" content={description} key="description" />
      ) : null}

      {props.keywords ? (
        <meta name="keywords" content={props.keywords} key="keywords" />
      ) : null}

      {canonicalUrl ? (
        <link rel="canonical" href={canonicalUrl} key="canonical" />
      ) : null}

      {robotsContent ? (
        <meta name="robots" content={robotsContent} key="robots" />
      ) : null}

      {/* Open Graph */}
      <meta property="og:title" content={ogTitle} key="og:title" />
      {ogDescription ? (
        <meta
          property="og:description"
          content={ogDescription}
          key="og:description"
        />
      ) : null}
      <meta property="og:type" content={ogType} key="og:type" />
      {props.openGraph?.image ? (
        <>
          <meta property="og:image" content={props.openGraph.image} key="og:image" />
          <meta
            property="og:image:secure_url"
            content={props.openGraph.image}
            key="og:image:secure_url"
          />
        </>
      ) : null}
      {ogUrl ? <meta property="og:url" content={ogUrl} key="og:url" /> : null}

      {/* Twitter */}
      <meta name="twitter:card" content={twCard} key="twitter:card" />
      <meta name="twitter:title" content={twTitle} key="twitter:title" />
      {twDescription ? (
        <meta
          name="twitter:description"
          content={twDescription}
          key="twitter:description"
        />
      ) : null}
      {props.twitter?.image ? (
        <meta name="twitter:image" content={props.twitter.image} key="twitter:image" />
      ) : null}
      {twUrl ? <meta name="twitter:url" content={twUrl} key="twitter:url" /> : null}

      {/* JSON-LD */}
      {props.jsonLd?.map((block, idx) => (
        <script
          key={`jsonld-${idx}`}
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(block) }}
        />
      ))}

      {props.children}
    </Head>
  );
}

